import * as React from 'react';
import { useField, useForm } from 'react-final-form';

import { FfpAccountFieldEnum, ValidationRuleFragment } from '@websky/graphql';
import { DateField } from '../../../../../../Passenger/components/Field/Date/Date';
import { Select } from '../../../../../../Passenger/components/Field/Select/Select';
import { InputType, PassengerFieldOption } from '../../../../../../Passenger/types';
import { validate } from '../../../../../../Passenger/utils';
import { useTranslation } from 'react-i18next';
import Input from '../../../../../../Input';
import PhoneInput from '../../../../../../PhoneInput';
import Dropdown from '../../../../../../BaseComponents/Dropdown';
import { AutocompleteInputChangeReason } from '@mui/material';
import GenderSwitch from '../../../../../../TravellerForm/components/GenderSwitch/GenderSwitch';

const ns = 'Passenger';

interface Props {
	name: string;
	value?: string;
	label: string;
	type: InputType;
	validationRules: ValidationRuleFragment[];
	fieldType?: FfpAccountFieldEnum;
	options?: PassengerFieldOption[];
	translateValue?: boolean;
	disabled?: boolean;
	onAutocompleteInputChange?: (value: string, reason: AutocompleteInputChangeReason) => void;
}

const Field: React.FC<Props> = props => {
	const { t } = useTranslation(ns);
	const form = useForm();

	const field = useField(props.name, {
		type: 'text',
		validate: (value: string, allValues) => {
			return t(
				validate(
					value,
					allValues,
					props.type,
					props.validationRules,
					props.fieldType,
					undefined,
					undefined,
					ns
				) as string
			);
		}
	});

	const isError = field.meta.error && field.meta.touched;
	const error = isError ? field.meta.error : undefined;

	if (props.type === InputType.Phone) {
		return (
			<PhoneInput
				value={field.input.value}
				textFieldsProps={{
					...field.input,
					onChange: field.input.onChange,
					name: field.input.name,
					error: isError,
					helperText: error,
					label: t(props.label)
				}}
			/>
		);
	}

	if (props.type === InputType.Switch) {
		return (
			<GenderSwitch
				value={field.input.value}
				onChange={field.input.onChange}
				onBlur={field.input.onBlur}
				error={error}
			/>
		);
	}

	if (props.type === InputType.Date) {
		return (
			<DateField
				label={t(props.label)}
				value={field.input.value}
				onChange={field.input.onChange}
				onBlur={field.input.onBlur}
				error={error}
			/>
		);
	}

	if (props.type === InputType.Select) {
		let val = field.input.value;
		if (props.name === 'docType') {
			const isExist = props.options.some(option => option.value === val);
			if (!isExist) {
				val = null;
			}
		}

		return (
			<>
				<Select
					options={props.options}
					name={props.name}
					label={t(props.label)}
					selectFirstOptionByDefault={props.name === 'docType'}
					onChange={async (key, e) => {
						if (props.name === 'country') {
							form.change('docType', null);
						}
						field.input.onChange(e);
					}}
					onBlur={field.input.onBlur}
					value={val}
					error={error}
					translateValue={props.translateValue}
				/>
			</>
		);
	}

	if (props.type === InputType.Autocomplete) {
		return (
			<Dropdown
				InputProps={{
					...field.input,
					label: t(props.label),
					error: isError,
					helperText: error
				}}
				options={props.options.map(option => ({ label: option.key, value: option.value }))}
				disabled={props.disabled}
				onChange={(_, value) => {
					field.input.onChange(value || null);
				}}
				onInputChange={(_, value, reason) => props.onAutocompleteInputChange(value, reason)}
				// disable inner filter
				filterOptions={(options, _) => options}
				noOptionsText={field.input.value ? t('Nothing was found for your request') : t('Start typing')}
			/>
		);
	}

	return (
		<Input
			label={t(props.label)}
			value={field.input.value}
			name={field.input.name}
			TextFieldProps={{
				...field.input,
				error: isError,
				helperText: error,
				disabled: props.disabled
			}}
		/>
	);
};

export default Field;
